<template>
  <!-- 文本框 -->
  <template v-if="item.searchType == undefined || item.searchType == 'text'">
    <Input
      v-model:value="searchParam[item.prop!]"
      :placeholder="item.placeholder === undefined ? `${$lang('请输入')}${item.label}` : `${item.placeholder}`"
      type="text"
      :maxlength="item.maxLength || 9999"
      :allowClear="item.allowClear"
      @blur="item.onBlur"
      @change="item.onChange"
      :disabled="item.disabled"
      :suffix="item.suffix"
      :prefix="item.prefix"
    ></Input>
  </template>
  <template v-if="item.searchType === 'InputNumber'">
    <InputNumber
      v-model:value="searchParam[item.prop!]"
      :min="item.min"
      :max="item.max"
      :defaultValue="item.defaultValue"
      :precision="item.precision"
      :controls="false"
      :placeholder="item.placeholder === undefined ? `${$lang('请输入')}${item.label}` : `${item.placeholder}`"
      :maxlength="item.maxLength || 9999"
      :allowClear="item.allowClear"
      @blur="item.onBlur"
      :disabled="item.disabled"
      :addon-after="item.addonAfter"
      :addon-before="item.addonBefore"
    >
    </InputNumber>
  </template>

  <template v-if="item.searchType == 'slot'">
    <slot :name="item.prop"></slot>
  </template>
  <!-- 文本域 -->
  <template v-if="item.searchType == 'textarea'">
    <Textarea
      v-model:value="searchParam[item.prop!]"
      :placeholder="item.placeholder === undefined ? `${$lang('请输入')}${item.label}` : `${item.placeholder}`"
      type="text"
    ></Textarea>
  </template>
  <!-- 下拉选择框 -->
  <template v-if="item.searchType == 'select'">
    <Select
      v-if="!item.remote"
      v-model:value="searchParam[item.prop!]"
      :mode="item.mode"
      :allowClear="item.allowClear"
      :placeholder="`${$lang('请选择')}${item.label}`"
      @change="item.onChange"
      :disabled="item.disabled"
      :field-names="item.fieldNames"
      :options="item.options"
    >
    </Select>

    <!-- 远程模糊查询 -->
    <Select
      v-if="item.remote"
      show-search
      v-model:value="searchParam[item.prop!]"
      :allowClear="item.allowClear"
      :filterOption="filterOption"
      :placeholder="`${item.placeholder}`"
      @search="item.onSearch"
      @change="item.onChange"
      @keyup.enter="item.onEnter"
    >
      <template v-if="item.field === 'serviceSiteCode'">
        <SelectOption
          v-for="itemValue in item.options"
          :key="itemValue.value"
          :value="itemValue.serviceSiteCode"
          :label="itemValue.serviceSiteName"
        >
          {{ itemValue.serviceSiteName + ' | ' + itemValue.serviceSiteCode }}
        </SelectOption>
      </template>

      <template v-if="item.field === 'channelName'">
        <SelectOption
          v-for="itemValue in item.options"
          :key="itemValue.value"
          :value="itemValue.channelName"
          :label="itemValue.channelName"
        >
          {{ itemValue.channelName }}
        </SelectOption>
      </template>
    </Select>
  </template>
  <!-- 级联选择器 -->
  <template v-if="item.searchType === 'cascader'">
    <Cascader
      v-model:value="searchParam[item.prop!]"
      change-on-select
      :options="item.options"
      @change="item.onChange"
      :disabled="item.disabled"
      :placeholder="`${$lang('请选择')}${item.label}`"
      :allow-clear="item.allowClear"
    />
  </template>
  <!-- 下拉树形选择框 -->
  <template v-if="item.searchType == 'treeSelect' || item.searchType == 'multipleTreeSelect'">
    <TreeSelect
      v-model:value="searchParam[item.prop!]"
      :multiple="item.searchType == 'multipleTreeSelect'"
      :data="item.options"
    />
  </template>
  <!-- 日期选择 -->
  <template v-if="item.searchType == 'date'">
    <DatePicker
      v-model:value="searchParam[item.prop!]"
      value-format="YYYY-MM-DD"
      type="date"
      :placeholder="`${$lang('请选择')}${item.label}`"
    />
  </template>
  <!-- 时间范围选择 -->
  <template v-if="item.searchType == 'timerange'">
    <TimePicker
      v-model:value="searchParam[item.prop!]"
      is-range
      value-format="HH:mm:ss"
      :range-separator="$lang('至')"
      :start-placeholder="$lang('开始时间')"
      :end-placeholder="$lang('结束时间')"
    />
  </template>
  <!-- 日期范围选择 -->
  <template v-if="item.searchType == 'daterange'">
    <RangePicker
      v-model:value="searchParam[item.prop!]"
      type="daterange"
      :disabled-date="disabledDate"
      value-format="YYYY-MM-DD"
      :range-separator="$lang('至')"
      :start-placeholder="$lang('开始时间')"
      :end-placeholder="$lang('结束时间')"
      @change="datePickerChange"
      @calendarChange="onCalendarChange"
      :allowClear="typeof item.allowClear === 'undefined' || item.allowClear"
    />
  </template>
  <!-- 日期时间范围选择 -->
  <template v-if="item.searchType == 'datetimerange'">
    <RangePicker
      v-model:value="searchParam[item.prop!]"
      type="datetimerange"
      :disabled-date="disabledDate"
      :show-time="{
        hideDisabledOptions: true,
        defaultValue: [dayjs('00:00:00', 'HH:mm:ss')]
      }"
      format="YYYY-MM-DD HH:mm"
      value-format="YYYY-MM-DD HH:mm:ss"
      :range-separator="$lang('至')"
      :start-placeholder="$lang('开始时间')"
      :end-placeholder="$lang('结束时间')"
      @change="rangePickerChange"
      @calendarChange="onCalendarChange"
    />
  </template>
</template>

<script setup lang="ts" name="SearchFormItem">
  import { reactive } from 'vue'
  import {
    Input,
    Textarea,
    Select,
    SelectOption,
    TreeSelect,
    DatePicker,
    TimePicker,
    RangePicker,
    Cascader,
    InputNumber
  } from 'ant-design-vue'
  import { ColumnProps } from '@/components/model/tableModel'
  import { queryDate } from '@/hooks/useTime/index'
  import dayjs, { Dayjs } from 'dayjs'
  interface SearchFormItem {
    item: Partial<ColumnProps> // 具体每一个搜索项的配置
    searchParam: any // 搜索参数
  }
  const props = defineProps<SearchFormItem>()

  //props 转响应的不可直接更改props
  const item = reactive(props.item)
  const searchParam = reactive(props.searchParam)

  //选择时间rangePicker 将接口真实字段拼接
  const rangePickerChange = (value: any[]) => {
    const [start, end] = value || []
    const obj: any = {}
    obj[item.realField![0]] = start
    obj[item.realField![1]] = end
    Object.assign(searchParam, obj)
  }
  /* 日期范围字段拼接 */
  const datePickerChange = (value: any[]) => {
    const [start, end] = value?.length ? queryDate(value) : []
    const obj: any = {}
    obj[item.realField![0]] = start
    obj[item.realField![1]] = end
    Object.assign(searchParam, obj)
  }
  const onCalendarChange = (val: any) => {
    searchParam[item.prop!] = val
  }

  const disabledDate = (current: Dayjs) => {
    let dates = searchParam[item.prop!]
    if (typeof item.FomatDisabledDate === 'function') {
      return item.FomatDisabledDate(current, dates)
    } else {
      if (!dates || (dates as any).length === 0) {
        return current > dayjs().endOf('day') || current < dayjs().subtract(6, 'month')
      }
      const maxDay = item.limitDay || 30
      const tooLate = dates[0] && current.diff(dates[0], 'days') > maxDay
      const tooEarly = dates[1] && dayjs(dates[1]).diff(current, 'days') > maxDay
      return tooEarly || tooLate || current > dayjs().endOf('day')
    }
  }

  /* 模糊查询匹配符合条件的选项 */
  const filterOption = (input: string, option: any) => {
    return (
      option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
      option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
    )
  }
</script>
<style lang="less" scoped>
  .cascaderClass {
    width: 300px;
  }
</style>
